import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { NotesIconLink } from '../../components/ui/NotesIconLink';
import { ViewIconLink } from '../../components/ui/ViewIconLink';
import { usePermissions } from '../../contexts/PermissionsContext';
import { MyTeamOverview, getMyTeamOverview } from '../../services/myTeamApi';

function memberRoleLabel(member: {
  isImmediateManager: boolean;
  isCurrentUser: boolean;
  isSubordinate: boolean;
  isTeamLeader: boolean;
}): string {
  const parts: string[] = [];
  if (member.isImmediateManager) parts.push('Gestor imediato');
  if (member.isCurrentUser) parts.push('Você');
  if (member.isSubordinate) parts.push('Equipe');
  if (member.isTeamLeader && !member.isImmediateManager) parts.push('Líder');
  return parts.join(' · ') || '—';
}

export function MyTeamPage() {
  const { canAccessModule } = usePermissions();
  const canOpenNotes = canAccessModule('people.notes');
  const [overview, setOverview] = useState<MyTeamOverview | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        setOverview(await getMyTeamOverview());
      } catch {
        toast.error('Falha ao carregar a equipe.');
        setOverview(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <PageShell>
      <PageHeader
        title="Minha Equipe"
        description="Colaboradores da sua equipe, com acesso ao cadastro e anotações."
      />

      {loading ? (
        <p className="text-secondary-500">Carregando equipe...</p>
      ) : !overview ? (
        <p className="text-secondary-500">Não foi possível carregar a equipe.</p>
      ) : (
        <div className="space-y-4">
          {!overview.isManager ? (
            <section className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              Você ainda não possui subordinados vinculados (como líder de equipe ou chefe direto).
              A listagem abaixo mostra você e, se houver, seu gestor imediato.
            </section>
          ) : null}

          <section className="rounded-xl border border-secondary-200 bg-white p-4">
            <div className="mb-3">
              <h2 className="text-lg font-semibold text-secondary-900">Colaboradores</h2>
              <p className="text-sm text-secondary-500">
                {overview.members.length} pessoa{overview.members.length === 1 ? '' : 's'} no escopo da
                sua equipe.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="erp-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Cargo</th>
                    <th>Equipes</th>
                    <th>Gestores</th>
                    <th>Papel</th>
                    <th className="w-28">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {overview.members.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-secondary-500">
                        Nenhum colaborador no escopo.
                      </td>
                    </tr>
                  ) : (
                    overview.members.map((member) => (
                      <tr key={member.userId}>
                        <td>
                          <div className="font-medium text-secondary-900">{member.name}</div>
                          <div className="text-sm text-secondary-500">{member.email}</div>
                        </td>
                        <td>{member.jobTitle || '—'}</td>
                        <td>{member.teamNames.join(', ') || '—'}</td>
                        <td>{member.managerNames.join(', ') || '—'}</td>
                        <td>{memberRoleLabel(member)}</td>
                        <td>
                          <div className="flex items-center gap-1">
                            {canOpenNotes && member.isSubordinate ? (
                              <NotesIconLink
                                to={`/performance/notes?aboutUserId=${member.userId}`}
                                label="Anotações da equipe"
                              />
                            ) : null}
                            <ViewIconLink
                              to={`/people/my-team/members/${member.userId}`}
                              label="Ver cadastro"
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}
    </PageShell>
  );
}
